/**
 * Puts text on the clipboard. Text still being fetched (a secret) goes in as
 * a promise inside a ClipboardItem: Safari only lets a page write to the
 * clipboard during the click itself, and that form keeps the click's
 * permission while the request finishes. Where that form isn't supported,
 * it waits for the text and writes it plainly.
 */
export async function copyText(text: string | Promise<string>): Promise<void> {
  // The Clipboard API only exists on secure origins (https, localhost).
  if (!navigator.clipboard) return copyWithSelection(await text);
  if (typeof text === "string") return navigator.clipboard.writeText(text);
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "text/plain": text.then((t) => new Blob([t], { type: "text/plain" })) }),
      ]);
      return;
    } catch {
      // If fetching the text is what failed, rethrow that rather than retry.
      await text;
    }
  }
  await navigator.clipboard.writeText(await text);
}

/** The older route: select the text in a hidden field and ask the browser to copy it. */
function copyWithSelection(text: string): void {
  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  const ok = document.execCommand("copy");
  field.remove();
  if (!ok) throw new Error("the browser didn't allow it");
}
